/**
 * Rasterize a GeoJSON polygon (outer ring + optional holes) into a binary mask.
 *
 * Uses a scanline fill algorithm — for each row, compute edge intersections,
 * sort them, and fill between pairs.
 *
 * @param rings - GeoJSON coordinate rings: [outerRing, ...holeRings]
 *   Each ring is [[x0,y0],[x1,y1],...]. Outer ring is CCW, holes are CW.
 * @param width - Mask width in pixels
 * @param height - Mask height in pixels
 * @returns Binary mask Uint8Array(width * height), 1 = inside polygon
 */
export function rasterizePolygon(rings: [number, number][][], width: number, height: number): Uint8Array {
  const mask = new Uint8Array(width * height)

  // Collect all edges from all rings (outer + holes work via even-odd fill)
  const edges: Array<{ yMin: number; yMax: number; xAtYMin: number; invSlope: number }> = []

  for (const ring of rings) {
    if (!ring || ring.length < 3) continue
    for (let i = 0; i < ring.length; i++) {
      const [x0, y0] = ring[i]!
      const [x1, y1] = ring[(i + 1) % ring.length]!

      // Skip horizontal edges
      if (Math.round(y0) === Math.round(y1)) continue

      const yMin = Math.min(y0, y1)
      const yMax = Math.max(y0, y1)
      const xAtYMin = y0 < y1 ? x0 : x1
      const invSlope = (x1 - x0) / (y1 - y0)

      edges.push({ yMin, yMax, xAtYMin, invSlope })
    }
  }

  // Scanline fill with even-odd rule
  const intersections: number[] = []
  for (let row = 0; row < height; row++) {
    const scanY = row + 0.5 // sample at pixel center

    // Find all edge intersections at this scanline
    intersections.length = 0
    for (const edge of edges) {
      if (scanY >= edge.yMin && scanY < edge.yMax) {
        const x = edge.xAtYMin + (scanY - edge.yMin) * edge.invSlope
        intersections.push(x)
      }
    }

    // Sort intersections left to right
    intersections.sort((a, b) => a - b)

    // Fill between pairs (even-odd rule)
    for (let i = 0; i < intersections.length - 1; i += 2) {
      const xStart = Math.max(0, Math.ceil(intersections[i]!))
      const xEnd = Math.min(width - 1, Math.floor(intersections[i + 1]!))
      for (let col = xStart; col <= xEnd; col++) {
        mask[row * width + col] = 1
      }
    }
  }

  return mask
}

/**
 * Write a 2D binary mask into a 3D volume bitmap at the correct slice position.
 *
 * @param mask2d - Binary mask from rasterizePolygon (width * height)
 * @param sliceWidth - Width of the 2D mask
 * @param sliceHeight - Height of the 2D mask
 * @param pixelToVoxel - Maps (col, row) → [vx, vy, vz] in volume space
 * @param volumeDims - Volume dimensions [dimX, dimY, dimZ] (0-indexed sizes)
 * @param drawBitmap - The 3D volume bitmap (Uint8Array) to write into
 * @param penValue - Value to write (1 = paint, 0 = erase)
 */
export function applyMask2dToVolume(
  mask2d: Uint8Array,
  sliceWidth: number,
  sliceHeight: number,
  pixelToVoxel: (col: number, row: number) => [number, number, number],
  volumeDims: [number, number, number],
  drawBitmap: Uint8Array,
  penValue: number
): void {
  const [dimX, dimY] = volumeDims
  const strideY = dimX
  const strideZ = dimX * dimY

  for (let row = 0; row < sliceHeight; row++) {
    for (let col = 0; col < sliceWidth; col++) {
      if (mask2d[row * sliceWidth + col] !== 1) continue

      const [vx, vy, vz] = pixelToVoxel(col, row)
      const linearIdx = vx + vy * strideY + vz * strideZ
      if (linearIdx >= 0 && linearIdx < drawBitmap.length) {
        drawBitmap[linearIdx] = penValue
      }
    }
  }
}
